import { Component, afterNextRender, Injector } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from './core/services/storage.service';
import { MultilingualService } from './core/services/api/multilingual.service';
import { SsrStateService } from './core/services/ssr-state.service';
import {
    BRAND,
    getLocalLanguagesInKeyArray,
    getLocalLanguagesInValueArray,
    LOCAL_TRANSLATION_LANGUAGE,
} from './core/constants/app.constant';
import { MultilingualDropdownModel } from './core/models/multilingual.model';
import { ErrorService } from './core/services/error.service';
import { forkJoin, firstValueFrom } from 'rxjs';

import { RemoteConfig, getRemoteConfig, fetchAndActivate, getValue } from '@angular/fire/remote-config';
import { FirebaseApp } from '@angular/fire/app';

/**
 * Root application component that handles core initialization and SSR/hydration logic.
 * 
 * This component is responsible for:
 * - Language initialization with SSR-safe caching
 * - Firebase Remote Config setup (client-side only)
 * - SVG icon registration
 * - Page title management
 * - Route-based scroll behavior
 * 
 * **SSR Considerations:**
 * - Uses TransferState via SsrStateService to cache language data between server and client
 * - Implements client-side detection using afterNextRender() to avoid hydration mismatches
 * - Skips API calls during SSR to prevent self-signed certificate issues
 * - Falls back to cached or default language during server-side rendering
 * - Preloads default translations immediately to prevent translation key flash
 * 
 * @example
 * ```typescript
 * // This component is automatically instantiated as the root component
 * // No direct instantiation required
 * ```
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent {
    private remoteConfig!: RemoteConfig;
    private app: FirebaseApp | null = null;
    private firebaseInitialized = false;
    private languageApiCalled = false;

    protected pageTitle = 'TaxPod.Web.Customer';
    protected isMaintenance = false;
    protected previousUrl = '';
    protected availableLanguages: MultilingualDropdownModel[] = [];
    protected fallbackLanguage = LOCAL_TRANSLATION_LANGUAGE.EN;

    /**
     * Initializes the AppComponent with required dependencies and sets up client-side initialization.
     * 
     * **SSR Impact:**
     * - Constructor runs on both server and client
     * - Sets default language (en-US) immediately to prevent untranslated text flash
     * - Uses afterNextRender() to ensure client-side code only runs after hydration
     * - Initializes icons immediately (safe for both server and client)
     * 
     * @param activatedRoute - Angular router service for route information
     * @param domSanitizer - Angular service for sanitizing SVG resources
     * @param errorService - Custom service for error handling
     * @param matIconRegistry - Angular Material service for registering custom icons
     * @param multilingualService - Custom service for language management
     * @param router - Angular router service for navigation
     * @param storageService - Custom service for local storage operations
     * @param titleService - Angular service for managing page titles
     * @param translateService - ngx-translate service for internationalization
     * @param ssrStateService - Custom service for SSR state management
     * @param injector - Angular injector for dependency injection
     */
    constructor(
        private activatedRoute: ActivatedRoute,
        private domSanitizer: DomSanitizer,
        private errorService: ErrorService,
        private matIconRegistry: MatIconRegistry,
        private multilingualService: MultilingualService,
        private router: Router,
        private storageService: StorageService,
        private titleService: Title,
        private translateService: TranslateService,
        private ssrStateService: SsrStateService,
        private injector: Injector
    ) {
        // Developer Note: Uncomment in next production deployment.
        // Set default language immediately to prevent untranslated text flash
        // This runs synchronously on both server and client
        this.translateService.use(this.fallbackLanguage);

        // Consolidated client-side initialization.
        // This callback does not get called from server-side in SSR.
        afterNextRender(() => {
            this.initializeClientSideFeatures();
            this.scrollToTopInNewRoute();
        });

        this.initIcons();
    }

    /**
     * Initializes client-side only features after hydration is complete.
     * 
     * **SSR Impact:**
     * - Only runs on client-side after afterNextRender() callback
     * - Uses flags to prevent duplicate initialization
     * - Safely initializes Firebase and language features that require browser APIs
     * 
     * @private
     */
    private initializeClientSideFeatures(): void {
        // Initialize Firebase (only once)
        if (!this.firebaseInitialized) {
            this.firebaseInitialized = true;
            this.initializeFirebaseApp();
        }

        // Initialize language features (only once)
        if (!this.languageApiCalled) {
            this.languageApiCalled = true;
            this.initLanguage();
        }
    }

    /**
     * Registers custom SVG icons with Angular Material's MatIconRegistry.
     * 
     * **SSR Impact:**
     * - Safe to run during SSR as it only registers icon definitions
     * - Does not access browser-specific APIs
     * - Icons are available immediately after registration on both server and client
     * 
     * @private
     */
    private initIcons(): void {
        const icons = [
            { name: 'alert', path: '../assets/images/icons/alert.svg' },
            { name: 'arrow-right-keyboard', path: '../assets/images/icons/arrow-right-keyboard.svg' },
            { name: 'arrow-right-short', path: '../assets/images/icons/arrow-right-short.svg' },
            { name: 'arrow-right', path: '../assets/images/icons/arrow-right.svg' },
            { name: 'book', path: '../assets/images/icons/book.svg' },
            { name: 'calendar-invalid', path: '../assets/images/icons/calendar-invalid.svg' },
            { name: 'circled_accept', path: '../assets/images/icons/circled_accept.svg' },
            { name: 'clock-line', path: '../assets/images/icons/clock-line.svg' },
            { name: 'clock', path: '../assets/images/icons/clock.svg' },
            { name: 'close', path: '../assets/images/icons/close.svg' },
            { name: 'double-tick', path: '../assets/images/icons/icon-double-tick.svg' },
            { name: 'download-file', path: '../assets/images/icons/download_file.svg' },
            { name: 'dropdown-expand', path: '../assets/images/icons/dropdown_expand.svg' },
            { name: 'enrol', path: '../assets/images/icons/enrol.svg' },
            { name: 'file', path: '../assets/images/icons/file.svg' },
            { name: 'filter', path: '../assets/images/icons/filter.svg' },
            { name: 'fire', path: '../assets/images/icons/fire.svg' },
            { name: 'heart', path: '../assets/images/icons/heart.svg' },
            { name: 'my-certificates', path: '../assets/images/icons/my_certificates.svg' },
            { name: 'my-favourite', path: '../assets/images/icons/my_favourite.svg' },
            { name: 'my-subscription-billing', path: '../assets/images/icons/my_subscription_billing.svg' },
            { name: 'my-preferences', path: '../assets/images/icons/my-preferences.svg' },
            { name: 'settings', path: '../assets/images/icons/settings-cogwheel.svg' },
            { name: 'play', path: '../assets/images/icons/play.svg' },
            { name: 'play-btn', path: '../assets/images/icons/play-btn.svg' },
            { name: 'polygon', path: '../assets/images/icons/polygon.svg' },
            { name: 'rating-star', path: '../assets/images/icons/rating_star.svg' },
            { name: 'search', path: '../assets/images/icons/search.svg' },
            { name: 'trash-fill', path: '../assets/images/icons/trash_fill.svg' },
            { name: 'trash', path: '../assets/images/icons/trash.svg' },
            { name: 'user-profile', path: '../assets/images/icons/profile.svg' },
            { name: 'video-camera', path: '../assets/images/icons/video-camera.svg' },
            { name: 'information', path: '../assets/images/icons/information.svg' },
            { name: 'my-training-cpe-hours', path: '../assets/images/icons/my-training-cpe-hours.svg' },
            { name: 'my-programs', path: '../assets/images/icons/my-programs.svg' },
            { name: 'outline-email', path: '../assets/images/icons/outline-email.svg' },
            { name: 'outline-check', path: '../assets/images/icons/outline-check.svg' },
            { name: 'linkedin', path: '../assets/images/icons/linkedin.svg' },
        ];

        icons.forEach((icon) =>
            this.matIconRegistry.addSvgIcon(icon.name, this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path)),
        );
    }

    /**
     * Initializes Firebase Remote Config for maintenance mode and feature flags.
     * 
     * **SSR Impact:**
     * - Only runs on client-side (afterNextRender does not get called  on server-side)
     * - Skipped during SSR to avoid Firebase initialization issues
     * - Uses window object detection as additional safety measure
     * - Gracefully handles Firebase unavailability with error logging
     * 
     * @async
     * @private
     * @throws {Error} If Firebase initialization fails (caught and logged)
     */
    async initializeFirebaseApp() {
        if (typeof window !== 'undefined') {
            try {
                this.app = this.injector.get(FirebaseApp, null);
                if (!this.app) {
                    // Debug Log
                    console.warn('Firebase app not available, skipping Firebase initialization');
                    return;
                }
        
                this.remoteConfig = getRemoteConfig(this.app);
                this.remoteConfig.settings = {
                    minimumFetchIntervalMillis: 0,
                    fetchTimeoutMillis: 6000
                };
                // ------------------------
                await fetchAndActivate(this.remoteConfig);

                // Debug Log
                //console.log('Remote config fetched and activated');
                // ------------------------
                this.isMaintenance = getValue(this.remoteConfig, 'isCustomerWebUnderMaintenance').asBoolean();
            }
            catch (error) {
                // Debug Log (Error)
                console.error('Firebase not available in this environment:', error);
            }
        }
    }

    /**
     * Initializes the application's language system with SSR-safe caching.
     * 
     * **SSR Impact:**
     * - Calls during afterNextRender, which only executes in client-side.
     * - Implements race condition prevention by checking cached data first
     * - Avoids API calls during SSR to prevent self-signed certificate issues
     * - Uses SsrStateService to maintain language consistency across SSR/hydration boundary
     * 
     * **Flow:**
     * 1. Client: Check TransferState cache → API call if needed → Filter local languages → Set translation
     * 2. Server: Use cached/default language → Skip API calls → Set translation without cache update
     * 
     * @async
     * @private
     * @throws {Error} If language initialization fails (handled gracefully with fallbacks)
     */
    private async initLanguage(): Promise<void> {
        // Skip API call during SSR to avoid self-signed certificate issues.
        // (Verified to have consistently occur in localhost as of 12th September 2025, did not test in remote environments).
        // Take the logic outside of the if-else statement to experience the issue first-hand.
        if (typeof window !== 'undefined') {
            // ------------------
            await this.getAvailableLanguagesFromServer();

            // Debug Log
            // console.log("[On App Init] Available Languages (Before Local Languages Filter): ", this.availableLanguages);

            // "en-US", "zh-CN"
            let availableLocalLanguages = getLocalLanguagesInValueArray();

            // Debug Log
            // console.log("[On App Init] Available Local Languages: ", availableLocalLanguages);
            
            this.availableLanguages = this.availableLanguages.filter((item) => availableLocalLanguages.includes(item.formattedLocal));
            
            // Debug Log
            // console.log("[On App Init] Available Languages (After Local Languages Filter): ", this.availableLanguages);

            if (this.availableLanguages.length <= 0) {
                // Debug Log (Error)
                console.error("[On App Init] No Available Languages Detected.");
                return;
            }
            // ------------------
            this.translateService.addLangs(this.availableLanguages.map(lang => lang.formattedLocal));
            this.multilingualService.availableLanguage.next(this.availableLanguages);
            // ------------------
            // Developer Note: By right there should only be 1 language from the database
            // set to true as default.
            // If there are more, the bottom-most/last default language data row will be used.
            let startingLang: string | null = this.storageService.language ? 
                (this.availableLanguages.find(lang => lang.formattedLocal === this.storageService.language)?.formattedLocal || null): 
                null;
            
            if (!startingLang) {
                for (const lang of this.availableLanguages) {
                    if (!lang.isDefault) {
                        continue;
                    }
                    // Debug Log
                    // console.log("[On App Init] Switching to Newly Detected Default Language: ", lang);
        
                    startingLang = lang.formattedLocal;
                    break;
                }
            }

            if (!startingLang) {
                // Debug Log (Error)
                console.error("[On App Init] No Starting Language Found");
                return;
            }

            // Debug Log
            // console.log("[On App Init] Local Cache Language: ", this.storageService.language);

            // Debug Log
            // console.log("[On App Init] Starting Language: ", startingLang);

            this.setTranslationModuleLanguage(startingLang);
        }
        else {
            // During SSR, use cached language or default
            const cachedLanguage = this.storageService.language || this.fallbackLanguage;
            this.setTranslationModuleLanguage(cachedLanguage, false);
        }
        
        this.loadTitle();
        // ------------------
    }

    /**
     * Fetches available languages from the server with SSR-safe caching mechanism.
     * 
     * **SSR Impact:**
     * - Calls during afterNextRender, which only executes in client-side.
     * - Implements pure cached data approach to eliminate race conditions
     * - Checks TransferState cache first before making API calls
     * - Only makes HTTP requests if no cached data exists
     * - Stores results in TransferState for SSR/hydration consistency
     * - Provides fallback language data if API call fails
     * 
     * **Cache Strategy:**
     * 1. Check SsrStateService for existing cached languages
     * 2. If cached data exists, use it immediately (no API call)
     * 3. If no cache, make API call and store result in TransferState
     * 4. On error, use hardcoded fallback languages
     * 
     * @async
     * @private
     * @throws {Error} API call failures are caught and handled with fallback data
     */
    private async getAvailableLanguagesFromServer(): Promise<void> {
        // Check if we already have available languages in TransferState
        if (this.ssrStateService.hasAvailableLanguages()) {
            this.availableLanguages = this.ssrStateService.getAvailableLanguages() || [];
            return;
        }

        try {
            const response = await firstValueFrom(this.multilingualService.getMultilingualDropdown());
            this.availableLanguages = response.result.map(
                (item) => new MultilingualDropdownModel(item)
            );
        }
        catch (error) {
            // Debug Log (Error)
            console.error("Failed to initialize language from server.", error);

            // Generate available languages dynamically from LOCAL_TRANSLATION_LANGUAGE keys
            const languageKeys = getLocalLanguagesInKeyArray();
            const languageNameMap: { [key: string]: string } = {
                'EN': 'English',
                'CH': 'Chinese'
            };

            this.availableLanguages = languageKeys.map((key, index) => {
                return new MultilingualDropdownModel({
                    id: (index + 1).toString(),
                    languageName: languageNameMap[key] || key,
                    local: key,

                    // EN is the default language, based on "languageNameMap" order.
                    isDefault: index == 0
                });
            });

            // this.errorService.showError(error);
        }
                
        // Store in TransferState for SSR/hydration
        this.ssrStateService.setAvailableLanguages(this.availableLanguages);
    }

    /**
     * Sets the active language for the translation module and updates related services.
     * 
     * **SSR Impact:**
     * - Calls during afterNextRender, which only executes in client-side.
     * - `updateCache` parameter controls whether to update local storage
     * - During SSR: called with `updateCache: false` to avoid storage operations
     * - During client hydration: called with `updateCache: true` to persist language choice
     * 
     * @param lang - The language code to set (e.g., 'en-US', 'zh-CN')
     * @param updateCache - Whether to update local storage (default: true, false during SSR)
     * @private
     */
    private setTranslationModuleLanguage(lang: string, updateCache: boolean = true): void {
        if (updateCache) {
            this.storageService.setLanguage(lang);
        }

        this.translateService.setFallbackLang(lang);
        this.translateService.use(lang);
        
        // Notify multilingual service about the language change
        // This ensures header component gets updated with the correct language
        this.multilingualService.currentLanguage.next(lang);
        
        // Debug Log
        // console.log("[On App Init] Set Module Language To: ", lang);
    }

    /**
     * Loads and initializes page title translations for all supported languages.
     * 
     * **SSR Impact:**
     * - Calls during afterNextRender, which only executes in client-side.
     * - Uses forkJoin to load all language translations simultaneously
     * - Triggers setPageTitle() after translations are loaded
     * 
     * @private
     */
    private loadTitle(): void {
        const translationRequests = 
            Object.values(LOCAL_TRANSLATION_LANGUAGE).map(
                (lang) => this.translateService.get(lang),
            );

        forkJoin(translationRequests).subscribe({
            next: () => {
                this.setPageTitle();
            },
        });
    }

    /**
     * Sets up automatic scroll-to-top behavior on route navigation.
     * 
     * **SSR Impact:**
     * - Calls during afterNextRender, which only executes in client-side.
     * 
     * @private
     */
    private scrollToTopInNewRoute(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const currentUrl = this.router.url;
                const path = currentUrl.split('?')[0];

                if (path !== this.previousUrl) {
                    window.scrollTo(0, 0);
                }

                this.previousUrl = path;

                this.setPageTitle();
            }
        });
    }

    /**
     * Sets the browser page title based on current route and translations.
     * 
     * **SSR Impact:**
     * - Calls during afterNextRender, which only executes in client-side.
     * - Uses Angular's Title service which works on both server and client
     * - Dynamically constructs title from route data and brand constant
     * - Ensures proper title is set for search engine crawlers during SSR
     * 
     * @private
     */
    private setPageTitle(): void {
        let child = this.activatedRoute;

        while (child.firstChild) {
            child = child.firstChild;
        }

        const title = child.snapshot.data['title'] || null;

        this.pageTitle = title ? `${BRAND} - ${this.translateService.instant(title)}` : BRAND;

        this.titleService.setTitle(this.pageTitle);
    }
}
